import { IcChevronBackNegative } from '@assets/icons/24x24';
import { HStack, Icon, IconButton, Separator, Tabs, Text, VStack } from '@chakra-ui/react';
import SearchTab from '@components/cockpit/panels/notificationSearch/filterComponents/searchFilterTabs/SearchTab';
import SelectionTab from '@components/cockpit/panels/notificationSearch/filterComponents/searchFilterTabs/SelectionTab';
import { SearchFilterFieldName } from '@components/cockpit/panels/notificationSearch/notificationSearch.types';
import { useNotificationSearchContext } from '@components/cockpit/panels/notificationSearch/NotificationSearchContext';
import { useSearchFilterField } from '@components/cockpit/panels/notificationSearch/useSearchFilterField';
import { FormattedMessage, useIntl } from 'react-intl';

interface SearchFilterTriggerPanelProps {
  field: SearchFilterFieldName;
  showSuggestionMinInputLength?: number;
}

export function SearchFilterTriggerPanel({
  field,
  showSuggestionMinInputLength,
}: Readonly<SearchFilterTriggerPanelProps>) {
  const { formatMessage } = useIntl();

  const { setActiveSearchFilterField } = useNotificationSearchContext();
  const { selectedItems } = useSearchFilterField(field);
  const typeLabel = formatMessage({ id: `components.notificationSearch.filters.searchFilters.type.${field}.plural` });

  return (
    <VStack width="100%" flex={1} backgroundColor="neutral.800" gap={0}>
      <HStack
        width="100%"
        height="56px"
        minHeight="56px"
        flexShrink={0}
        paddingLeft={3}
        paddingRight={0}
        color="neutral.200"
        gap={2}
      >
        <IconButton
          variant="transparent"
          boxSize="24px"
          aria-label="back"
          onClick={() => setActiveSearchFilterField(null)}
        >
          <Icon as={IcChevronBackNegative} boxSize="24px" />
        </IconButton>
        <Text textTransform="uppercase" fontSize="sm">
          <FormattedMessage
            id="components.notificationSearch.filters.searchFilters.select"
            values={{ type: typeLabel }}
          />
        </Text>
      </HStack>
      <Separator borderColor="neutral.850" />
      <Tabs.Root variant="light" size="md" gap={0.25} backgroundColor="neutral.800" defaultValue="search">
        <Tabs.List backgroundColor="neutral.800" justifyContent="start" paddingLeft={3} gap={4}>
          <Tabs.Trigger value="search" backgroundColor="neutral.800">
            <Text textTransform="capitalize">
              <FormattedMessage id="components.notificationSearch.filters.searchFilters.search" />
            </Text>
          </Tabs.Trigger>
          <Tabs.Trigger value="selection" backgroundColor="neutral.800">
            <HStack gap={1}>
              <Text textTransform="capitalize">
                <FormattedMessage id="components.notificationSearch.filters.searchFilters.selections" />
              </Text>
              <Text color="blue">{selectedItems.length}</Text>
            </HStack>
          </Tabs.Trigger>
        </Tabs.List>
        <Tabs.ContentGroup>
          <Tabs.Content value="search">
            <SearchTab field={field} showSuggestionMinInputLength={showSuggestionMinInputLength} />
          </Tabs.Content>
          <Tabs.Content value="selection">
            <SelectionTab field={field} />
          </Tabs.Content>
        </Tabs.ContentGroup>
      </Tabs.Root>
    </VStack>
  );
}
